import React from "react";
import { FORM_ITEM_CHECKBOX } from "../../../components/Form/FormItems/FormItemCheckbox";
import { FORM_ITEM_DATE_PICKER } from "../../../components/Form/FormItems/FormItemDatePicker";
import { FORM_ITEM_RESOURCE_SELECT } from "../../../components/Form/FormItems/FormItemResourceSelect";
import { FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING } from "../../../components/Form/FormItems/FormItemResourceSelectLazyLoading";
import { FORM_ITEM_TEXT } from "../../../components/Form/FormItems/FormItemText";
import { FORM_ITEM_TEXT_AREA } from "../../../components/Form/FormItems/FormItemTextArea";
import { FORM_TYPE_UPLOAD_FILE } from "../../../components/Form/FormItems/FormItemUploadFile";
import {
  createDisabledDateBefore,
  createDisabledDateAfter
} from "../../Core/Users/User/UserFormSteps/datePeriodHelpers";
import EmployeeFieldWithCheckbox from "./EmployeeFieldWithCheckbox";

/**
 * Builds the complete list of form field definitions for the QualificationForm modal.
 *
 * Returns common fields (employee, training, description, expiry notification, internal remarks)
 * plus create-only fields (dates, instructor, uploads) appended when `isNewRecord` is true.
 * Edit mode restricts users to only updating expiry notification and internal remarks.
 *
 * @param {object}   params
 * @param {object}   params.data         - Modal data: user details, qualification record, instructor.
 * @param {object}   params.training     - Currently selected training (drives expiry checkbox visibility).
 * @param {boolean}  params.isNewRecord  - Whether we are creating a new qualification.
 * @param {object}   params.form         - Ant Design form instance.
 * @param {Function} params.t            - react-i18next translation function.
 * @param {Function} params.setTraining  - Setter to update the training state on selection change.
 * @returns {Array} Array of field definition objects for renderDynamicallyFields.
 */
export const buildQualificationFormFields = ({ data, training, isNewRecord, form, t, setTraining }) => [
  {
    name: "employee_field",
    customField: (
      <EmployeeFieldWithCheckbox
        form={form}
        value={data.user_detail?.user_id ? `${data.name} (${data.user_detail.staff_number})` : undefined}
        disabled={!isNewRecord}
        t={t}
      />
    )
  },
  {
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "training_id",
    label: "trg.add_qualification_modal.training",
    placeholder: "trg.add_qualification_modal.training",
    apiRoute: "trg/trainings",
    initialValue: data.qualification?.training
      ? {
          value: data.qualification.training.id,
          text: `${data.qualification.training.name} (${data.qualification.training.code})`
        }
      : undefined,
    fieldProps: {
      searchColumns: ["name", "code"],
      disabled: !isNewRecord,
      onChange: (id, obj) => setTraining(obj),
      customParams: {
        with: ["provider", "validity"],
        order_by_column: ["name"],
        order_by_type: ["asc"],
        field: ["active"],
        op: ["eq"],
        term: [1]
      },
      transformer: (values) => values.map((item) => ({ ...item, text: `${item.name} (${item.code})` }))
    }
  },
  {
    type: FORM_ITEM_TEXT_AREA,
    name: "description",
    label: "trg.planning_mass_assignment.description",
    placeholder: "trg.planning_mass_assignment.description",
    autoSize: { minRows: 2, maxRows: 6 },
    fieldProps: { disabled: !isNewRecord }
  },
  {
    type: FORM_ITEM_CHECKBOX,
    name: "expiry_notification",
    label: "trg.add_qualification_modal.expiry_notification",
    // Hide this checkbox when the selected training is configured with "no expiry" validity
    visible: training ? !training.validity?.no_expiry : true,
    fieldProps: { defaultChecked: true }
  },
  {
    type: FORM_ITEM_TEXT_AREA,
    name: "internal_remarks",
    label: "trg.add_qualification_modal.internal_remarks",
    placeholder: "trg.add_qualification_modal.internal_remarks",
    autoSize: { minRows: 2, maxRows: 6 }
  },
  // The following fields are only relevant when creating a new qualification record.
  // Editing an existing qualification does not allow historical dates or instructor data to be changed.
  ...(isNewRecord
    ? [
        {
          type: FORM_ITEM_DATE_PICKER,
          required: true,
          dependencies: ["validity_start_date"],
          name: "completed_date",
          label: "trg.add_qualification_modal.completed_date",
          placeholder: "trg.add_qualification_modal.completed_date",
          disabledDate: createDisabledDateBefore(form, "validity_start_date")
        },
        {
          type: FORM_ITEM_DATE_PICKER,
          required: true,
          dependencies: ["completed_date"],
          name: "validity_start_date",
          label: "trg.add_qualification_modal.validity_start_date",
          placeholder: "trg.add_qualification_modal.validity_start_date",
          disabledDate: createDisabledDateAfter(form, "completed_date")
        },
        {
          type: FORM_ITEM_RESOURCE_SELECT,
          required: true,
          name: "instructor_type",
          label: "trg.add_qualification_modal.instructor_type",
          placeholder: "trg.add_qualification_modal.instructor_type",
          options: [
            { value: 1, text: t("trg.add_qualification_modal.internal") },
            { value: 0, text: t("trg.add_qualification_modal.external") }
          ]
        },
        {
          required: true,
          type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
          visible: (formInstance) => formInstance.getFieldValue("instructor_type") === 1,
          name: "instructor_id",
          label: "trg.add_qualification_modal.instructor",
          placeholder: "trg.add_qualification_modal.instructor",
          initialValue: data.instructor,
          apiRoute: "users/all",
          fieldProps: {
            searchColumns: ["name"],
            customParams: {
              "filters[validContract]": 1,
              order_by_column: ["name"],
              order_by_type: ["asc"],
              field: ["user_is_in_organisation"],
              op: ["eq"],
              term: [1]
            }
          }
        },
        {
          required: true,
          type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
          visible: (formInstance) => formInstance.getFieldValue("instructor_type") === 0,
          name: "external_provider_id",
          label: "trg.add_qualification_modal.provider",
          placeholder: "trg.add_qualification_modal.provider",
          apiRoute: "trg/external-providers",
          fieldProps: {
            requestOnMount: !!data.external_provider_id,
            customParams: {
              order_by_column: ["name"],
              order_by_type: ["asc"],
              field: ["active"],
              op: ["eq"],
              term: [1]
            }
          }
        },
        {
          required: true,
          type: FORM_ITEM_TEXT,
          visible: (formInstance) => formInstance.getFieldValue("instructor_type") === 0,
          max: 200,
          name: "instructor_name",
          label: "trg.add_qualification_modal.instructor",
          placeholder: "trg.add_qualification_modal.instructor"
        },
        {
          type: FORM_TYPE_UPLOAD_FILE,
          name: "evidence",
          label: "trg.add_qualification_modal.evidence",
          placeholder: "ior.new_report.new_attachment.attachment_button",
          fieldProps: {
            purpose: "qualificationHistory",
            entity: "qualificationHistory",
            type: "document",
            placeholder: t("container.general_words.drag_and_drop_file"),
            dragAndDrop: true
          }
        },
        {
          type: FORM_TYPE_UPLOAD_FILE,
          name: "certificate",
          label: "trg.add_qualification_modal.certificate",
          placeholder: "ior.new_report.new_attachment.attachment_button",
          fieldProps: {
            purpose: "certificate",
            entity: "certificate",
            type: "document",
            placeholder: t("container.general_words.drag_and_drop_file"),
            dragAndDrop: true
          }
        }
      ]
    : [])
];
